// Date helpers for the public site.
//
// The data files are written by the exporter against Europe/Helsinki midnight:
// foodstoday.json holds the Helsinki "today", foodstomorrow.json the day after.
// Every date this UI derives therefore has to use the same zone. Reading the
// device clock instead lets a phone in another timezone disagree with the file
// it just downloaded — and nothing in the UI would reveal the disagreement.

const DAY_MS = 24 * 60 * 60 * 1000;

const WEEKDAYS = [
  "Sunnuntai",
  "Maanantai",
  "Tiistai",
  "Keskiviikko",
  "Torstai",
  "Perjantai",
  "Lauantai",
];

// An explicit timeZone is the only way to get Helsinki's calendar date without
// shipping a tz database. Wrapped because a browser without timezone support
// throws on construction, and falling back to the device clock beats no site.
let formatter = null;
try {
  formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Helsinki",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
} catch (e) {
  formatter = null;
}

// Helsinki's calendar date, offset by whole days, carried as a UTC midnight so
// day arithmetic and getUTCDay() can't drift with the device's own offset.
export function helsinkiDay(offsetDays) {
  const offset = offsetDays || 0;
  let year, month, day;

  if (formatter) {
    const parts = {};
    formatter.formatToParts(new Date()).forEach((part) => {
      if (part.type !== "literal") parts[part.type] = Number(part.value);
    });
    year = parts.year;
    month = parts.month;
    day = parts.day;
  } else {
    const local = new Date();
    year = local.getFullYear();
    month = local.getMonth() + 1;
    day = local.getDate();
  }

  return new Date(Date.UTC(year, month - 1, day) + offset * DAY_MS);
}

export function weekdayName(date) {
  return WEEKDAYS[date.getUTCDay()];
}

// Split a "YYYY-MM-DD" into numbers. The exporter zero-pads, but older files in
// the wild do not ("2020-09-9"), so compare numbers rather than strings.
function isoParts(isoDate) {
  const parts = String(isoDate || "")
    .split("-")
    .map(Number);
  if (parts.length !== 3 || parts.some((n) => !isFinite(n))) return null;
  return parts;
}

export function isSameDay(isoDate, date) {
  const parts = isoParts(isoDate);
  if (!parts) return false;
  return (
    parts[0] === date.getUTCFullYear() &&
    parts[1] === date.getUTCMonth() + 1 &&
    parts[2] === date.getUTCDate()
  );
}

export function isoToDate(isoDate) {
  const parts = isoParts(isoDate);
  if (!parts) return null;
  return new Date(Date.UTC(parts[0], parts[1] - 1, parts[2]));
}

// "25.9."
export function shortIsoDate(isoDate) {
  const date = isoToDate(isoDate);
  if (!date) return "";
  return date.getUTCDate() + "." + (date.getUTCMonth() + 1) + ".";
}

// "Perjantai 25.9." — built from the data's own DATE, so it states what is
// actually on screen rather than what the day buttons imply.
export function describeIsoDate(isoDate) {
  const date = isoToDate(isoDate);
  if (!date) return "";
  return weekdayName(date) + " " + shortIsoDate(isoDate);
}
